import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, MessageCircle, MapPin, Clock, Trash2, CheckCircle, Calendar, Phone } from 'lucide-react';
import { toast } from 'sonner';
import ChatConversation from '../components/ChatConversation';
import Navbar from '../components/Navbar';

export default function PartnerBoardPage() {
  const { API, user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ text: '', area: '', preferred_time: '', level: '', game_date: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [chatUser, setChatUser] = useState(null);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/partner-board`);
      setPosts(res.data.posts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const handleCreate = async () => {
    if (!form.text.trim()) { toast.error('כתוב מה אתה מחפש 🎾'); return; }
    if (!form.level) { toast.error('יש לבחור רמה'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/partner-board`, form, authHeaders);
      toast.success('המודעה פורסמה! 🎾');
      setFormOpen(false);
      setForm({ text: '', area: '', preferred_time: '', level: '', game_date: '', phone: '' });
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בפרסום');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (id) => {
    try {
      await axios.patch(`${API}/partner-board/${id}/close`, {}, authHeaders);
      toast.success('מעולה! שיהיה משחק טוב 🎾');
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק את המודעה?')) return;
    try {
      await axios.delete(`${API}/partner-board/${id}`, authHeaders);
      toast.success('המודעה נמחקה');
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה');
    }
  };

  const timeAgo = (iso) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `לפני ${Math.max(1, mins)} דק׳`;
    if (mins < 1440) return `לפני ${Math.floor(mins / 60)} שע׳`;
    return `לפני ${Math.floor(mins / 1440)} ימים`;
  };

  const waLink = (phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return null;
    const intl = digits.startsWith('972') ? digits : digits.startsWith('0') ? '972' + digits.slice(1) : digits;
    return `https://wa.me/${intl}`;
  };

  const fmtGameDate = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const active = posts.filter((p) => p.status === 'active');
  const closed = posts.filter((p) => p.status === 'closed');

  const PostCard = ({ p }) => {
    const mine = user && p.user_id === user.id;
    const isClosed = p.status === 'closed';
    return (
      <Card className={`transition-all hover:shadow-lg ${isClosed ? 'opacity-70' : ''}`} data-testid={`partner-post-${p.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Link to={`/profile/${p.user_id}`}>
              <Avatar className="w-12 h-12 border-2 border-emerald-200">
                {p.user_avatar && <AvatarImage src={p.user_avatar} alt={p.user_name} />}
                <AvatarFallback className="bg-emerald-100 text-emerald-700 font-bold">{(p.user_name || '?').charAt(0)}</AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/profile/${p.user_id}`} className="font-bold text-gray-800 hover:text-emerald-600">{p.user_name}</Link>
                {(p.level || p.skill_level) && <Badge variant="outline" className="text-blue-600 border-blue-200">{p.level || p.skill_level}</Badge>}
                {isClosed && <Badge className="bg-emerald-500">נמצא שותף 🎉</Badge>}
                <span className="text-xs text-gray-400 mr-auto">{timeAgo(p.created_at)}</span>
              </div>
              <p className="mt-2 text-gray-700 whitespace-pre-wrap leading-relaxed">{p.text}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500 flex-wrap">
                {p.game_date && <span className="flex items-center gap-1 font-bold text-purple-600"><Calendar className="w-4 h-4" />{fmtGameDate(p.game_date)}</span>}
                {p.area && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-emerald-500" />{p.area}</span>}
                {p.preferred_time && <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-blue-500" />{p.preferred_time}</span>}
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {!mine && !isClosed && user && (
                  <Button
                    size="sm"
                    onClick={() => setChatUser({ id: p.user_id, name: p.user_name })}
                    className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                    data-testid={`contact-poster-${p.id}`}
                  >
                    <MessageCircle className="w-4 h-4 ml-1" />
                    💬 שלח הודעה
                  </Button>
                )}
                {!mine && !isClosed && p.phone && waLink(p.phone) && (
                  <a href={waLink(p.phone)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-poster-${p.id}`}>
                    <Button size="sm" className="bg-[#25D366] hover:bg-[#1ebe5b] rounded-full">
                      <Phone className="w-4 h-4 ml-1" />
                      ווצאפ
                    </Button>
                  </a>
                )}
                {!user && !isClosed && (
                  <Link to="/auth"><Button size="sm" variant="outline" className="rounded-full">התחבר כדי להגיב</Button></Link>
                )}
                {mine && !isClosed && (
                  <Button size="sm" variant="outline" onClick={() => handleClose(p.id)} className="rounded-full text-emerald-600 border-emerald-300" data-testid={`close-post-${p.id}`}>
                    <CheckCircle className="w-4 h-4 ml-1" />
                    מצאתי שותף
                  </Button>
                )}
                {(mine || user?.is_admin) && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-600 rounded-full" data-testid={`delete-post-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-white" dir="rtl" data-testid="partner-board-page">
      <Navbar />
      <div className="py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-1.5 text-sm font-bold mb-3">
            <Users className="w-4 h-4" />
            קהילת השחקנים
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">🎾 מחפש שותף</h1>
          <p className="text-gray-500">מצא פרטנר למשחק — פרסם מודעה או הגב למודעה קיימת בצ'אט</p>
        </div>

        <div className="flex justify-center mb-8">
          {user ? (
            <Button onClick={() => setFormOpen(true)} className="bg-gradient-to-l from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-full px-8 py-6 text-lg font-bold shadow-lg" data-testid="new-post-btn">
              <Plus className="w-5 h-5 ml-2" />
              פרסם מודעה
            </Button>
          ) : (
            <Link to="/auth"><Button variant="outline" className="rounded-full px-8">התחבר כדי לפרסם מודעה</Button></Link>
          )}
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-12">טוען...</p>
        ) : active.length === 0 && closed.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <span className="text-5xl block mb-3">🎾</span>
            אין מודעות כרגע — היה הראשון לפרסם!
          </div>
        ) : (
          <div className="space-y-4">
            {active.map((p) => <PostCard key={p.id} p={p} />)}
            {closed.length > 0 && (
              <>
                <h3 className="text-sm font-bold text-gray-400 pt-4">🎉 מצאו שותף לאחרונה</h3>
                {closed.map((p) => <PostCard key={p.id} p={p} />)}
              </>
            )}
          </div>
        )}
      </div>

      {/* טופס מודעה חדשה */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-xl">🎾 מודעה חדשה</DialogTitle>
            <DialogDescription>ספר לקהילה מה אתה מחפש</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">מה אתה מחפש? *</Label>
              <Textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="לדוגמה: מחפש שותף למשחק אימון ביום שלישי בערב, רמה בינונית+"
                rows={3}
                maxLength={500}
                data-testid="post-text-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">רמה *</Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="post-level-select">
                  <SelectValue placeholder="בחר רמה" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="דרג א">דרג א (רמה 4-5)</SelectItem>
                  <SelectItem value="דרג ב">דרג ב (רמה 5-6)</SelectItem>
                  <SelectItem value="דרג ג">דרג ג (רמה 6-7)</SelectItem>
                  <SelectItem value="יחידים open">יחידים Open (רמה 3-4)</SelectItem>
                  <SelectItem value="זוגות open">זוגות Open (כל הרמות)</SelectItem>
                  <SelectItem value="נשים">נשים (רמה 5-7)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">תאריך המשחק</Label>
              <Input
                type="date"
                value={form.game_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setForm({ ...form, game_date: e.target.value })}
                data-testid="post-date-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">אזור / מגרש</Label>
              <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="לדוגמה: ראשון לציון" data-testid="post-area-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">מתי נוח לך?</Label>
              <Input value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} placeholder="לדוגמה: ימי שלישי/חמישי אחרי 19:00" data-testid="post-time-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">טלפון לווצאפ (לא חובה)</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="לדוגמה: 050-1234567"
                dir="ltr"
                className="text-right"
                data-testid="post-phone-input"
              />
              <p className="text-xs text-gray-400 mt-1">אם תוסיף - יופיע כפתור ווצאפ ירוק על המודעה</p>
            </div>
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full py-5 font-bold" data-testid="submit-post-btn">
              {saving ? 'מפרסם...' : 'פרסם מודעה 🎾'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* צ'אט עם המפרסם */}
      {chatUser && (
        <ChatConversation
          open={!!chatUser}
          onClose={() => setChatUser(null)}
          otherUserId={chatUser.id}
          otherUserName={chatUser.name}
        />
      )}
      </div>
    </div>
  );
}
